import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Lock, Check, Clock, Phone } from "lucide-react";
import { api } from "../lib/api";

const DEPOSIT = 25;
const QUICKBOOKS_URL = "https://connect.intuit.com/t/scs-v1-05e10d9081f143c890d08af2771db0c7b4943785acd64dba9718733428e82d3e03095284ee8a4e55b28a874ad11bbdd5";

export default function Deposit() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [secs, setSecs] = useState(600);

  useEffect(() => {
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");

  const checkout = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/deposit-lead", { name, email });
    } catch {
      // Non-blocking: still proceed to QuickBooks payment
    }
    window.location.href = QUICKBOOKS_URL;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <div className="max-w-md w-full mx-auto px-5 py-8 flex-1 flex flex-col">
        <div className="flex items-center justify-center">
          <span className="font-heading font-black uppercase text-sm tracking-tight">Auto Spa On Wheels<span className="text-[#F5A623]">.</span></span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10"
        >
          <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl tracking-tighter leading-[0.9]">
            Secure Your<br />Appointment<span className="text-[#F5A623]">.</span>
          </h1>

          <a href="https://share.google/KHd6gUIaoIit5qEPw" target="_blank" rel="noreferrer"
            data-testid="deposit-reviews"
            className="mt-6 inline-flex items-center gap-3 border border-[#1a1a1a] bg-[#0a0a0a] rounded-full pl-4 pr-5 py-2.5 hover:border-[#F5A623] transition-colors">
            <span className="flex gap-0.5 text-[#F5A623]">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#F5A623" />)}</span>
            <span className="font-mono text-xs text-zinc-300">4.9 · 66 reviews →</span>
          </a>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Your Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="deposit-name"
                className="w-full bg-[#0a0a0a] border border-[#222] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-[#F5A623] transition-colors" placeholder="Jane Doe" />
            </label>
            <label className="block">
              <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Email for Confirmation</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="deposit-email"
                className="w-full bg-[#0a0a0a] border border-[#222] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-[#F5A623] transition-colors" placeholder="you@email.com" />
            </label>
          </div>

          <div className="mt-8 border border-[#1a1a1a] bg-[#0a0a0a] rounded-xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Deposit Summary</div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-zinc-300">Appointment Deposit</span>
              <span className="font-heading font-black text-2xl tracking-tight" data-testid="deposit-amount">${DEPOSIT}.00</span>
            </div>
            <p className="mt-2 text-xs text-zinc-500">This deposit is applied toward your total service cost.</p>
            <ul className="mt-5 space-y-2.5 border-t border-[#1a1a1a] pt-5">
              {["Locks in your appointment date & time", "Applied toward your total service balance", "Confirmation sent to your email"].map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm text-zinc-300">
                  <Check size={14} className="text-[#F5A623] shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 font-mono text-xs text-zinc-500">
            <Clock size={13} className="text-[#F5A623]" />
            <span data-testid="deposit-timer" className="text-[#F5A623]">{mm}:{ss}</span> left to secure your booking
          </div>

          <button onClick={checkout} disabled={loading} data-testid="deposit-book-now"
            className="mt-4 w-full rounded-full bg-[#F5A623] py-4 font-mono text-sm font-bold uppercase tracking-widest text-black hover:bg-[#ffb733] transition-colors disabled:opacity-60">
            {loading ? "Redirecting…" : `Book Now · $${DEPOSIT}`}
          </button>

          <div className="mt-5 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
            <Lock size={12} /> Secured by QuickBooks
          </div>
          <div className="mt-6 text-center">
            <a href="tel:+17192719913" className="font-mono text-xs text-zinc-400 hover:text-[#F5A623] inline-flex items-center gap-1.5">
              <Phone size={12} /> Questions? (719) 271-9913
            </a>
            <p className="mt-4 text-[11px] text-zinc-600">This deposit is non-refundable.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
